import Dexie from 'dexie'

import { logError } from '@/shared/logger'
import {
  NetworkSpecificSchema,
  DexieModels,
} from '@/shared/Database/NetworkSpecific/NetworkSpecificSchema'
import { AuthenticatedApp } from '@/shared/models/AuthenticatedApp'
import { Restriction } from '@/shared/models/Restriction'
import {
  UpAddress,
  EoaAddress,
  PopupProfile,
  FrontendProfile,
  LastPopupState,
  BackgroundProfile,
  SeenPermissionChanges,
  DigitalAsset,
} from '@/shared/models/Profile'
import { Transaction } from '@/shared/models/Transaction'
import { Origin } from '@/shared/models/Origin'
import { Sign } from '@/shared/models/Sign'
import { PermissionWhitelist } from '@/shared/Permissions'
import { AppNotification, ToastType } from '@/shared/models/AppNotification'
import { NetworkId } from '@/shared/models/Network'
import { GasProvider } from '@/shared/models/GasProvider'
import { Controller } from '../../models/Controller'
import { UpGenerateLSP23Data_DEPRECATED } from '../../models/GenerateLSP23address'
import { SomethingWentWrongError } from '../../models/SomethingWentWrongError'
import { RelayServiceDBSchema } from '@/shared/Database/UPSettings/models'

export class NetworkSpecificDatabase {
  private schema: NetworkSpecificSchema

  get isAlive(): boolean {
    return this.schema.isOpen()
  }

  get name(): string {
    return this.schema.name
  }

  static async load(networkId: NetworkId): Promise<NetworkSpecificDatabase> {
    const schema = new NetworkSpecificSchema(networkId)
    try {
      await schema.open()
    } catch (error) {
      logError('Error loading action database', error)
    }
    return new NetworkSpecificDatabase(schema)
  }

  constructor(schema: NetworkSpecificSchema) {
    this.schema = schema
  }

  private getTable = <T extends keyof DexieModels>(
    name: T
  ): Dexie.Table<any, any> => {
    return this.schema[name]
  }

  put = async <T extends keyof DexieModels>(
    property: T,
    object: DexieModels[T]
  ): Promise<void> => {
    await this.getTable(property).put(object)
  }

  bulkPut = async <T extends keyof DexieModels>(
    property: T,
    objects: DexieModels[T][]
  ): Promise<void> => {
    await this.getTable(property).bulkPut(objects)
  }

  clear = async <T extends keyof DexieModels>(property: T): Promise<void> => {
    await this.getTable(property).clear()
  }

  closeDatabase() {
    this.schema.close()
  }

  // ------------------------ Profiles ------------------------

  async getProfile(address: string): Promise<BackgroundProfile | undefined> {
    return await this.schema.profiles.get(address)
  }

  async getProfiles(): Promise<BackgroundProfile[]> {
    return this.schema.profiles.toArray()
  }

  async deleteProfile(address: string): Promise<void> {
    await this.schema.profiles.delete(address)
  }

  async updateLatestBlock(
    address: UpAddress,
    blockNumber: number
  ): Promise<void> {
    await this.schema.profiles.update(address, {
      latestBlock: blockNumber,
    })
  }

  async createProfile(profile: BackgroundProfile, popupProfile: PopupProfile) {
    return await this.schema.transaction(
      'rw',
      this.schema.profiles,
      this.schema.popupProfiles,
      async () => {
        await this.schema.profiles.put(profile)
        await this.schema.popupProfiles.put(popupProfile)
      }
    )
  }

  async updateDeviceControllerAddress(
    address: UpAddress,
    controllerAddress: EoaAddress
  ): Promise<void> {
    await this.schema.profiles.update(address, {
      controllerAddress: controllerAddress,
      controllerDirty: false,
    })
  }

  async getControllers(profileAddress: UpAddress): Promise<Controller[]> {
    return this.schema.controllers
      .where('profileAddress')
      .equals(profileAddress)
      .toArray()
  }

  async removeControllers(profileAddress: UpAddress): Promise<void> {
    await this.schema.controllers
      .where('profileAddress')
      .equals(profileAddress)
      .delete()
  }

  // ------------------------ Popup profiles ------------------------

  insertPopupProfile = async (profile: PopupProfile): Promise<void> => {
    await this.schema.popupProfiles.put(JSON.parse(JSON.stringify(profile)))
  }

  /**
   * Updates `saved` or `temporary` profile in the PopupProfile.
   *
   * @param address of the profile to update.
   * @param key holding the name of the property to update.
   * @param value new value for the property. Set to `undefined`/`null` to remove the current value.
   */
  updatePopupProfile = async (
    address: string,
    key: 'saved' | 'temporary',
    value?: Partial<FrontendProfile>
  ): Promise<void> => {
    const popupProfile = await this.schema.popupProfiles.get(address)
    const currentValue = popupProfile?.[key] as FrontendProfile | undefined
    if (!currentValue && !value) {
      return
    }
    await this.schema.popupProfiles.update(address, {
      [key]: {
        ...currentValue,
        ...value,
      },
    })
  }

  getPopupProfiles = async (): Promise<PopupProfile[]> => {
    return await this.schema.popupProfiles
      .filter(profile => !!profile.isOwner)
      .toArray()
  }

  getPopupProfilesCount = async (): Promise<number> => {
    return await this.schema.popupProfiles.count()
  }

  getPopupProfile = async (
    address: string
  ): Promise<PopupProfile | undefined> => {
    return await this.schema.popupProfiles.get(address)
  }

  deletePopupProfile = async (address: string): Promise<void> => {
    return await this.schema.popupProfiles.delete(address)
  }

  getLastPopupState = async (
    address?: string
  ): Promise<LastPopupState | undefined> => {
    if (!address) {
      return undefined
    }
    return await this.schema.lastPopupState.get(address)
  }

  removeLastPopupState = async (profileAddress: string): Promise<void> => {
    return await this.schema.lastPopupState.delete(profileAddress)
  }

  saveLastPopupState = async (
    profileAddress: string,
    changes: {
      [keyPath: string]: any
    }
  ): Promise<void> => {
    if (await this.schema.lastPopupState.get(profileAddress)) {
      await this.schema.lastPopupState.update(profileAddress, {
        ...changes,
        timestamp: Date.now(),
      })
    } else {
      try {
        await this.schema.lastPopupState.add({
          ...changes,
          path: changes.path,
          profileAddress,
          timestamp: Date.now(),
        })
      } catch {
        await this.schema.lastPopupState.update(profileAddress, {
          ...changes,
          timestamp: Date.now(),
        })
      }
    }
  }

  setDefaultGasProvider = async (
    address: UpAddress,
    gasProviderId?: string
  ) => {
    const popupProfile = await this.schema.popupProfiles.get(address)
    if (popupProfile) {
      await this.schema.popupProfiles.update(address, {
        saved: popupProfile.saved
          ? {
              ...popupProfile.saved,
              gasProviderId,
            }
          : undefined,
        temporary: popupProfile.temporary
          ? {
              ...popupProfile.temporary,
              gasProviderId,
            }
          : undefined,
      })
    }
  }

  getGasProviderIdForProfile = async (
    address: UpAddress
  ): Promise<RelayServiceDBSchema['id'] | undefined> => {
    const popupProfile = await this.getPopupProfile(address)
    const profile = popupProfile?.saved || popupProfile?.temporary
    return profile?.gasProviderId
  }

  // ------------------------ Transactions ------------------------

  async getTransactionByHash(hash: string): Promise<Transaction | undefined> {
    return await this.schema.transactions.get({ transactionHash: hash })
  }

  async getTransactionByRequestId(
    requestId: string
  ): Promise<Transaction | undefined> {
    return await this.schema.transactions.get({ requestId })
  }

  async getTransactions(address: string): Promise<Transaction[]> {
    return await this.schema.transactions
      .where('createdBy')
      .equals(address)
      .toArray()
  }

  async removeTransactionByHash(hash: string): Promise<void> {
    this.schema.transactions.where('transactionHash').equals(hash).delete()
  }

  async removeTransactionByRequestId(requestId: string): Promise<void> {
    this.schema.transactions.where('requestId').equals(requestId).delete()
  }

  async getInitializedTransactions(): Promise<Transaction[]> {
    return this.schema.transactions
      .filter(transaction => transaction.status === 'INITIALIZED')
      .toArray()
  }

  async getPendingTransactions(): Promise<Transaction[]> {
    return this.schema.transactions
      .filter(transaction => transaction.status === 'PENDING')
      .toArray()
  }

  async getPendingTransactionsByGasProvider(
    address: string,
    gasProvider?: GasProvider
  ): Promise<Transaction[]> {
    return await this.schema.transactions
      .filter(
        tx =>
          tx.createdBy === address &&
          tx.status === 'PENDING' &&
          tx.gasProvider?.type === gasProvider?.type &&
          tx.gasProvider?.id === gasProvider?.id
      )
      .toArray()
  }

  // ------------------------ AuthenticatedApps ------------------------

  async getAuthenticatedAppByOrigin(
    origin: string
  ): Promise<AuthenticatedApp | undefined> {
    return await this.schema.authenticated_apps
      .where('origin')
      .equals(origin)
      .first()
  }

  async getAllAuthenticatedAppsByOrigin(
    origin: string
  ): Promise<AuthenticatedApp[]> {
    return await this.schema.authenticated_apps
      .where('origin')
      .equals(origin)
      .toArray()
  }

  async getAuthenticatedAppsByAddressAndOrigin(
    profileAddress: UpAddress,
    origin: string
  ): Promise<AuthenticatedApp[]> {
    return await this.schema.authenticated_apps
      .filter(
        app => app.origin === origin && app.profileAddress === profileAddress
      )
      .toArray()
  }

  async getAuthenticatedApps(
    profileAddress: UpAddress
  ): Promise<AuthenticatedApp[]> {
    return await this.schema.authenticated_apps
      .where('profileAddress')
      .equals(profileAddress)
      .toArray()
  }

  async removeAuthenticatedApps(origin: string): Promise<void> {
    await this.schema.authenticated_apps.where('origin').equals(origin).delete()
  }

  // ------------------------ Custom controller names ------------------------

  async getCustomControllerName(
    profileAddress: UpAddress,
    controllerAddress: EoaAddress
  ): Promise<string | undefined> {
    const table = await this.schema.controllerCustomNames.get([
      profileAddress,
      controllerAddress,
    ])
    return table?.name
  }

  async saveCustomControllerName(
    profileAddress: UpAddress,
    controllerAddress: EoaAddress,
    controllerName: string
  ): Promise<void> {
    await this.schema.controllerCustomNames.put({
      profileAddress,
      controllerAddress,
      name: controllerName,
    })
  }

  // ------------------------ Notifications ------------------------

  async getNotifications(
    profileAddress: UpAddress
  ): Promise<AppNotification[]> {
    return this.schema.notifications
      .filter(
        notif =>
          notif.profileAddress === profileAddress ||
          notif.profileAddress === '' ||
          notif.profileAddress === undefined
      )
      .toArray()
  }

  async getNotificationById(id: string): Promise<AppNotification | undefined> {
    return this.schema.notifications.where('id').equals(id).first()
  }

  async getAllNotifications(): Promise<AppNotification[]> {
    return this.schema.notifications.toArray()
  }

  /**
   * Only counts notifications that are representing an event that's terminated.
   * @returns count of all notifications that don't have type set to 'LOADING'.
   */
  async getTerminalNotificationsCount(): Promise<number> {
    return await this.schema.notifications
      .where('type')
      .notEqual(ToastType.LOADING)
      .count()
  }

  async removeNotification(id: string) {
    return this.schema.notifications.delete(id)
  }

  async removeAllProfileNotifications(
    profileAddress: UpAddress
  ): Promise<number> {
    return await this.schema.notifications
      .where('profileAddress')
      .equals(profileAddress)
      .delete()
  }

  async clearAllLoadingNotifications(): Promise<void> {
    await this.schema.notifications.where('type').anyOf(['LOADING']).delete()
  }

  // ------------------------ AllowedCall ------------------------

  async getRestrictions(profileAddress: UpAddress): Promise<Restriction[]> {
    return this.schema.allowed_calls
      .where('profileAddress')
      .equals(profileAddress)
      .toArray()
  }

  async cleanRestrictions(
    profileAddress: UpAddress,
    appAddress: EoaAddress,
    type: PermissionWhitelist
  ): Promise<void> {
    await this.schema.allowed_calls
      .filter(
        allowedCall =>
          allowedCall.type === type &&
          allowedCall.profileAddress === profileAddress &&
          allowedCall.appAddress === appAddress
      )
      .delete()
  }

  // ------------------------ Signs ------------------------

  async getSigns(profileAddress: UpAddress): Promise<Sign[]> {
    return await this.schema.signs
      .where('profileAddress')
      .equals(profileAddress)
      .toArray()
  }

  // ------------------------ Origins ------------------------

  async getOrigin(url: string): Promise<Origin | undefined> {
    return await this.schema.origins.get({ url })
  }

  // ------------------------ PermissionsChangeWarning ------------------------

  async getPermissionChangeWarning(
    profileAddress: string
  ): Promise<SeenPermissionChanges | undefined> {
    return await this.schema.hasSeenPermissionChangeWarning.get({
      profileAddress,
    })
  }

  // ------------------------ AssetIcon ------------------------

  async getDigitalAsset(
    assetAddress?: string
  ): Promise<DigitalAsset | undefined> {
    return await this.schema.digitalAssets.get({
      assetAddress,
    })
  }

  // ------------------------ Configuration ------------------------

  async setSelectedProfile(selectedProfile?: UpAddress): Promise<void> {
    const config = await this.schema.configuration.toArray()
    if (config.length > 0) {
      const previousProfile = config[0].selectedProfile
      this.schema.configuration.update(config[0], {
        selectedProfile,
        previousProfile,
      })
    } else if (selectedProfile) {
      this.put('configuration', {
        id: 0,
        selectedProfile: selectedProfile,
      })
    }
  }

  async getSelectedProfile(): Promise<UpAddress | undefined> {
    const config = await this.schema.configuration.toArray()
    return config.length > 0 ? config[0].selectedProfile : undefined
  }

  async getPreviousProfile(): Promise<UpAddress | undefined> {
    const config = await this.schema.configuration.toArray()
    return config.length > 0 ? config[0].previousProfile : undefined
  }

  // ------------------------ Application Errors ------------------------
  async addSomethingWentWrongError(
    somethingWentWrongError: SomethingWentWrongError
  ): Promise<void> {
    await this.schema.somethingWentWrongErrors.put(somethingWentWrongError)
  }

  async getUnreadSomethingWentWrongErrors(): Promise<
    SomethingWentWrongError[]
  > {
    return this.schema.somethingWentWrongErrors
      .filter(error => !error.read)
      .toArray()
  }

  async readAllSomethingWentWrongErrors(): Promise<void> {
    await this.schema.somethingWentWrongErrors.toCollection().modify(error => {
      error.read = true
    })
  }

  // ------------------------ LSP23Data ------------------------
  // Required for migrations to work
  async getAllLSP23Data_DEPRECATED(): Promise<
    UpGenerateLSP23Data_DEPRECATED[]
  > {
    return this.getTable('generateLSP23Data' as any).toArray()
  }
}
